import { DesignTokens } from './design-tokens.interface';

export const DEFAULT_DESIGN_TOKENS: DesignTokens = {
  color: {
    primary: '#2CCAFF',
    primaryShade: '#23A2CC',           // darken(primary, 12%)  — hover/active
    primaryShadeHover: '#1E8AAD',      // darken(primaryShade, 15%) — hover
    primaryShadeActive: '#176985',     // darken(primaryShade, 35%) — pressed
    primaryShadeDark: '#027AA2',       // darken(primary, 25%)  — pressed/emphasis
    primaryTint: '#80DFFF',            // lighten(primary, 15%) — highlights
    primaryTintLight: 'rgba(21, 123, 246, 0.10)',  // opacity(primary, 10%) — subtle bg
    primaryTintLighter: 'rgba(21, 123, 246, 0.05)', // opacity(primary, 5%) — very subtle bg
    primaryTintMuted: '#D3E6FD',       // lighten(primary, 35%) — muted bg
    primaryPale: 'rgba(21, 123, 246, 0.04)', // opacity(primary, 4%) — barely visible bg
    primaryTint20: 'rgba(21, 123, 246, 0.20)', // opacity(primary, 20%) — chips/borders
    primaryShadow: 'rgba(21, 123, 246, 0.30)', // opacity(primary, 30%) — brand shadow

    secondary: '#157BF8',
    secondaryShade: '#0F56AC',         // darken(secondary, 15%) — hover/active
    secondaryShade2: '#0A3D7A',        // darken(secondaryShade, 30%) — pressed
    secondaryTint: '#5BA3F9',          // lighten(secondary, 15%) — highlights
    secondaryTint24: 'hsl(213deg 93% 67% / 24%)', // opacity(secondary, 24%) — subtle bg

    tertiary: '#04003A',
    tertiaryShade: '#030029',          // darken(tertiary, 5%)  — hover/active
    tertiaryTint: '#4F4D75',           // lighten(tertiary, 25%) — muted text

    accent: '#203BFF',
    accentLight: '#297BF8',            // lighten(accent, 20%) — soft variant

    error: '#CC0000',
    errorShade: '#8F0000',             // darken(error, 10%)  — hover/active
    errorTint: '#D63333',              // lighten(error, 10%) — soft variant
    errorSchemes: '#B3261E',           // Material Design error — darken(error, 5%)

    success: '#4BB543',
    successShade: '#3C9136',           // darken(success, 10%) — hover/active
    successTint: '#6FC469',            // lighten(success, 10%) — soft variant

    warning: '#FFCC00',
    warningShade: '#CCA300',           // darken(warning, 10%) — hover/active
    warningTint: '#FFDB4D',            // lighten(warning, 15%) — soft variant
    warningDark: '#4C3D00',            // darken(warning, 35%) — text on warning bg

    inactive: '#CCD1D2',
    inactiveShade: '#A8ADAD',          // darken(inactive, 10%) — hover/active
    inactiveTint: '#E4E6E7',           // lighten(inactive, 5%) — soft variant

    textPrimary: 'rgba(0, 0, 0, 0.70)',
    textSecondary: 'rgba(74, 74, 74, 0.70)',
    textDark: '#000000',

    backgroundPage: '#EFF2F5',
    backgroundGray: '#E8EAF5',
    backgroundGrayShade: '#BABBC4',    // darken(background-gray, 15%)
    backgroundGrayShade24: 'hsl(234deg 8% 75% / 24%)', // opacity(background-gray, 24%)
    backgroundGrayTint: '#EDEEF7',     // lighten(background-gray, 3%)
    backgroundCard: '#FFFFFF',
    backgroundInput: '#FFFFFF',
    backgroundNeutral: '#F5F5F5',

    strokeDefault: '#707070',
    strokeTable: '#E4E6E7',

    white: '#FFFFFF',
    shadowGray: 'rgba(4, 0, 58, 0.12)',
    shadowBlue: 'rgba(23, 64, 255, 0.15)',
  },

  // Patron: bg = opacity(color-base, 10%), text = darken(color-base, 40%)
  stateColors: {
    successBg: 'rgba(75, 181, 67, 0.10)',   // opacity(success, 10%)
    successText: '#1E481B',                  // darken(success, 40%)
    infoBg: 'rgba(44, 202, 255, 0.10)',      // opacity(primary, 10%)
    infoText: '#125166',                     // darken(primary, 40%)
    warningBg: 'rgba(255, 204, 0, 0.10)',    // opacity(warning, 10%)
    warningText: '#524100',                  // darken(warning, 40%)
    errorBg: 'rgba(204, 0, 0, 0.10)',        // opacity(error, 10%)
    neutralBg: 'rgba(74, 74, 74, 0.10)',     // opacity(gray, 10%)
  },

  chartColors: {
    purple: '#6B6BCE',
    limeGreen: '#62C694',
    iceBlue: '#ABCAE9',
    raspberry: '#D474A4',
    forestGreen: '#009990',
    mediumGreen: '#3EBB4F',
    solarYellow: '#F8C83C',
  },

  typography: {
    fontFamilyPrimary: "'Montserrat', sans-serif",
    fontFamilySecondary: "'ProductSans', sans-serif",
    fontSizeXs: '10px',
    fontSizeSm: '12px',
    fontSizeMd: '14px',
    fontSizeBase: '16px',
    fontSizeLg: '18px',
    fontSizeXl: '20px',
    fontSize2xl: '24px',
    fontSize3xl: '32px',
    fontWeightRegular: '400',
    fontWeightMedium: '500',
    fontWeightSemibold: '600',
    fontWeightBold: '700',
    lineHeightTight: '1.2',
    lineHeightNormal: '1.4',
    lineHeightRelaxed: '1.6',
  },

  spacing: {
    xxs: '4px',
    xs: '8px',
    sm: '12px',
    md: '16px',
    lg: '20px',
    xl: '24px',
    '2xl': '32px',
    '3xl': '40px',
    '4xl': '48px',
  },

  borders: {
    radiusSm: '4px',
    radiusMd: '8px',
    radiusLg: '16px',
    radiusXl: '25px',
    radiusPill: '48px',
    radiusFull: '9999px',
    widthThin: '1px',
    widthMedium: '2px',
  },

  shadows: {
    none: 'none',
    sm: '0px 0px 6px 0px rgba(0, 0, 0, 0.10)',
    md: '0px 0px 25px 0px rgba(23, 64, 255, 0.15)',
    card: '0px 2px 4px 0px rgba(4, 0, 58, 0.12), 0px 2px 16px 0px rgba(23, 64, 255, 0.15)',
    elevated: '0px 4px 12px rgba(21, 123, 246, 0.3)',
  },

  transitions: {
    durationFast: '0.15s',
    durationNormal: '0.2s',
    durationSlow: '0.3s',
    easingDefault: 'ease',
    easingInOut: 'ease-in-out',
  },

  gradients: {
    primary: 'linear-gradient(180deg, #203BFF 0%, #297BF8 100%)',
    border: 'linear-gradient(90deg, #203BFF 0%, #297BF8 100%)',
  },
};
